/**
 * Service for listing VA Clinical Notes via FHIR DocumentReference.
 * Binary retrieve and decode run on the Knowtex server when a patient_id is present
 * so decoded text can be cached. Client-side Binary fetch remains the fallback.
 */
import { getVAAccessToken } from "../utils/parseId";

export interface ExtractedProviderContext {
  duz: string;
  sta3n: string;
  name?: string;
  email?: string;
}

export interface RetrievedNoteMetadata {
  id: string;
  binaryId: string;
  binaryUrl: string;
  date: string;
  title: string;
  authorDisplay?: string;
  authorDuz?: string;
  docStatus?: string;
}

export interface DecodedNoteResult {
  text: string;
  metadata: RetrievedNoteMetadata;
  rawBinary?: Record<string, unknown>;
}

export type NoteRetrievalStep =
  "idle" | "connecting" | "searching" | "locating" | "retrieving" | "decoding" | "complete";

export interface NoteRetrievalProgress {
  step: NoteRetrievalStep;
  message: string;
}

/**
 * Decodes a base64 string to a UTF-8 string safely in both browser and test environments.
 */
export function decodeBase64Utf8(base64Str: string): string {
  const cleaned = (base64Str || "").replace(/\s+/g, "");
  if (!cleaned) return "";

  try {
    if (typeof Buffer !== "undefined") {
      return Buffer.from(cleaned, "base64").toString("utf-8");
    }
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      const binaryString = window.atob(cleaned);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      if (typeof TextDecoder !== "undefined") {
        return new TextDecoder("utf-8").decode(bytes);
      }
      return decodeURIComponent(
        Array.prototype.map
          .call(bytes, (byte: number) => "%" + ("00" + byte.toString(16)).slice(-2))
          .join("")
      );
    }
  } catch (error) {
    console.error("Failed to decode base64 string:", error);
    throw new Error("Unable to decode note content.");
  }
  return "";
}

/**
 * Decodes a JWT token payload without signature verification.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    if (!token) return null;
    const trimmed = token.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      return JSON.parse(trimmed);
    }
    const parts = trimmed.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const base64 = padded.replace(/-/g, "+").replace(/_/g, "/");
    const jsonStr = decodeBase64Utf8(base64);
    return JSON.parse(jsonStr);
  } catch (err) {
    console.error("Failed to decode JWT payload:", err);
    return null;
  }
}

/**
 * Resolves the FHIR base URL from local storage or environment default.
 */
export function getFhirBaseUrl(): string {
  const stored =
    typeof localStorage !== "undefined" ? localStorage.getItem("va_fhir_server") : null;
  if (stored && stored.trim().length > 0) {
    return stored.trim().replace(/\/+$/, "");
  }
  const envUrl = process.env.REACT_APP_VA_FHIR_SERVER;
  if (envUrl && envUrl.trim().length > 0) {
    return envUrl.trim().replace(/\/+$/, "");
  }
  return "https://sandbox-api.va.gov/services/clinical-fhir/v1/r4";
}

/**
 * Normalizes DUZ for robust string comparison.
 */
export function normalizeDuz(duz: unknown): string {
  if (duz === null || duz === undefined) return "";
  return String(duz).trim();
}

/**
 * Extracts provider DUZ, station ID, and metadata from the VA access token or local storage.
 */
export function extractProviderContextFromToken(
  token: string,
  preferredSta3n?: string
): ExtractedProviderContext {
  const payload = decodeJwtPayload(token) || {};
  let sta3n = "";
  let duz = "";

  // 1. Direct DUZ on token payload (e.g. payload.duz or payload.DUZ)
  if (payload.duz !== undefined && payload.duz !== null && String(payload.duz).trim()) {
    duz = normalizeDuz(payload.duz);
  } else if (payload.DUZ !== undefined && payload.DUZ !== null && String(payload.DUZ).trim()) {
    duz = normalizeDuz(payload.DUZ);
  }

  // 2. Parse vistaid claim if present
  // e.g. "500|520824994^PN^500^USVHA|A", "500:520824994", "500|520824994"
  const vistaidClaim = String(payload.vistaid || payload.vistaId || payload.VISTAID || "");

  if (vistaidClaim.trim().length > 0) {
    const entries = vistaidClaim.split(",");
    const targetSta3n = preferredSta3n
      ? String(preferredSta3n).trim()
      : typeof localStorage !== "undefined"
        ? String(localStorage.getItem("va_sta3n") || "").trim()
        : "";

    for (const entry of entries) {
      const [stationAndDuz] = entry.split("^");
      let rawSta3n = "";
      let rawDuz = "";

      if (stationAndDuz.includes("|")) {
        [rawSta3n, rawDuz] = stationAndDuz.split("|");
      } else if (stationAndDuz.includes(":")) {
        [rawSta3n, rawDuz] = stationAndDuz.split(":");
      } else {
        rawDuz = stationAndDuz;
      }

      const cleanSta3n = (rawSta3n || "").trim();
      const cleanDuz = (rawDuz || "").trim();

      if (cleanSta3n && cleanDuz) {
        if (!sta3n) sta3n = cleanSta3n;
        if (!duz) duz = cleanDuz;
        if (targetSta3n && cleanSta3n === targetSta3n) {
          sta3n = cleanSta3n;
          duz = cleanDuz;
          break;
        }
      } else if (cleanDuz && !duz) {
        duz = cleanDuz;
      }
    }
  }

  // 3. Fallbacks for DUZ if not yet extracted
  if (!duz) {
    duz =
      (typeof localStorage !== "undefined" ? localStorage.getItem("va_duz") || "" : "") ||
      normalizeDuz(payload.secid) ||
      normalizeDuz(payload.fediamsecid) ||
      normalizeDuz(payload.vauid);
  }

  // Fallbacks if not parsed from vistaid
  if (!sta3n) {
    sta3n =
      preferredSta3n ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("va_sta3n") || "" : "") ||
      "500";
  }

  return {
    duz,
    sta3n,
    name: typeof payload.name === "string" ? payload.name : undefined,
    email:
      typeof payload.preferred_username === "string"
        ? payload.preferred_username
        : typeof payload.email === "string"
          ? payload.email
          : undefined,
  };
}

export interface DocumentReferenceAuthor {
  type?: string;
  identifier?: {
    system?: string;
    value?: string;
  };
  display?: string;
}

export interface DocumentReferenceAttachment {
  contentType?: string;
  url?: string;
  title?: string;
  data?: string;
}

export interface DocumentReferenceResource {
  resourceType: "DocumentReference";
  id: string;
  docStatus?: string;
  status?: string;
  date?: string;
  description?: string;
  type?: {
    text?: string;
    coding?: Array<{
      display?: string;
      code?: string;
      system?: string;
    }>;
  };
  author?: DocumentReferenceAuthor[];
  content?: Array<{
    attachment?: DocumentReferenceAttachment;
  }>;
  context?: {
    period?: {
      start?: string;
      end?: string;
    };
  };
}

export interface DocumentReferenceBundle {
  resourceType?: string;
  type?: string;
  total?: number;
  entry?: Array<{
    resource?: DocumentReferenceResource;
  }>;
}

/**
 * Queries the FHIR DocumentReference endpoint for a patient and site.
 */
export async function fetchDocumentReferences(params: {
  patientIcn: string;
  site: string;
  accessToken: string;
  fhirUrl?: string;
}): Promise<DocumentReferenceResource[]> {
  const fhirUrl = params.fhirUrl || getFhirBaseUrl();
  const searchUrl = new URL(`${fhirUrl}/DocumentReference`);
  searchUrl.searchParams.set("patient", params.patientIcn);
  searchUrl.searchParams.set("-health-care-system", params.site);
  searchUrl.searchParams.set("docStatus", "final");

  let response = await fetch(searchUrl.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${params.accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  // If docStatus is rejected by an older endpoint, retry without it
  if (!response.ok && (response.status === 400 || response.status === 422)) {
    const fallbackUrl = new URL(`${fhirUrl}/DocumentReference`);
    fallbackUrl.searchParams.set("patient", params.patientIcn);
    fallbackUrl.searchParams.set("-health-care-system", params.site);
    response = await fetch(fallbackUrl.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${params.accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = (errorBody?.issue?.[0]?.diagnostics ||
      errorBody?.issue?.[0]?.details?.text ||
      errorBody?.message ||
      `VA FHIR DocumentReference request failed (${response.status})`) as string;
    throw new Error(message);
  }

  const bundle: DocumentReferenceBundle = await response.json();
  const entries = bundle.entry || [];
  return entries
    .map((e) => e.resource)
    .filter((r): r is DocumentReferenceResource =>
      Boolean(r && r.resourceType === "DocumentReference")
    );
}

export const MAX_CONCURRENT_NOTE_FETCHES = 5;
export const NO_SIGNED_NOTES_MESSAGE = "No signed clinical notes found for this patient.";
export const NO_PROVIDER_NOTES_MESSAGE =
  "No signed clinical notes found for your provider account.";

/**
 * Maps a DocumentReference to the metadata used by the picker and Binary fetch.
 */
export function mapDocumentReferenceToMetadata(
  doc: DocumentReferenceResource,
  targetDuz = ""
): RetrievedNoteMetadata {
  const primaryAttachment = doc.content?.[0]?.attachment;
  const rawUrl = primaryAttachment?.url || "";

  let binaryId = doc.id;
  if (rawUrl) {
    const match = rawUrl.match(/Binary\/([^?]+)/);
    if (match?.[1]) {
      binaryId = match[1];
    }
  }

  const primaryAuthor =
    doc.author?.find(
      (a) =>
        a.type === "Practitioner" ||
        (targetDuz && normalizeDuz(a.identifier?.value) === targetDuz)
    ) || doc.author?.[0];

  const title =
    primaryAttachment?.title ||
    doc.type?.text ||
    doc.type?.coding?.[0]?.display ||
    doc.description ||
    "Clinical Note";

  return {
    id: doc.id,
    binaryId,
    binaryUrl: rawUrl,
    date: doc.date || doc.context?.period?.start || "",
    title,
    authorDisplay: primaryAuthor?.display || "",
    authorDuz: normalizeDuz(primaryAuthor?.identifier?.value) || targetDuz,
    docStatus: doc.docStatus || "final",
  };
}

function compareDocsByDateDesc(
  a: DocumentReferenceResource,
  b: DocumentReferenceResource
): number {
  const dateStrA = a.date || a.context?.period?.start || "";
  const dateStrB = b.date || b.context?.period?.start || "";
  const timeA = dateStrA ? new Date(dateStrA).getTime() : 0;
  const timeB = dateStrB ? new Date(dateStrB).getTime() : 0;
  return timeB - timeA;
}

export function isSignedClinicalNote(doc: DocumentReferenceResource): boolean {
  const docStatus = (doc.docStatus || "").toLowerCase();
  if (docStatus !== "final") {
    return false;
  }

  return (doc.status || "").toLowerCase() !== "entered-in-error";
}

export function isProviderAuthoredNote(
  doc: DocumentReferenceResource,
  providerContext: ExtractedProviderContext
): boolean {
  const targetDuz = normalizeDuz(providerContext.duz);
  if (!targetDuz || !isSignedClinicalNote(doc)) {
    return false;
  }

  return (doc.author || []).some((a) => {
    const authorVal = normalizeDuz(a.identifier?.value);
    return Boolean(authorVal && authorVal === targetDuz);
  });
}

/**
 * All final notes for the patient, newest first. Any author.
 */
export function filterSignedNotes(docs: DocumentReferenceResource[]): RetrievedNoteMetadata[] {
  return docs
    .filter(isSignedClinicalNote)
    .sort(compareDocsByDateDesc)
    .map((doc) => mapDocumentReferenceToMetadata(doc));
}

/**
 * All final notes authored by this provider, newest first.
 */
export function filterNotesForProvider(
  docs: DocumentReferenceResource[],
  providerContext: ExtractedProviderContext
): RetrievedNoteMetadata[] {
  const targetDuz = normalizeDuz(providerContext.duz);
  if (!targetDuz) {
    return [];
  }

  return docs
    .filter((doc) => isProviderAuthoredNote(doc, providerContext))
    .sort(compareDocsByDateDesc)
    .map((doc) => mapDocumentReferenceToMetadata(doc, targetDuz));
}

export function groupNotesByTitle(
  notes: RetrievedNoteMetadata[]
): Array<{ title: string; notes: RetrievedNoteMetadata[] }> {
  const groups = new Map<string, RetrievedNoteMetadata[]>();

  for (const note of notes) {
    const title = (note.title || "Clinical Note").trim() || "Clinical Note";
    const existing = groups.get(title);
    if (existing) {
      existing.push(note);
    } else {
      groups.set(title, [note]);
    }
  }

  return Array.from(groups.entries())
    .sort(([leftTitle], [rightTitle]) =>
      leftTitle.localeCompare(rightTitle, "en", { sensitivity: "base" })
    )
    .map(([title, groupedNotes]) => ({
      title,
      notes: groupedNotes,
    }));
}

/**
 * Filters and sorts document references to locate the provider's most recent final note.
 * Per ADR (004-get-notes):
 * - Filters by author Practitioner DUZ (author.identifier.value === provider duz)
 * - Ensures note is final (docStatus is final; preliminary notes are left out)
 * - Sorts descending by date (most recent first) using date or context.period.start
 */
export function filterMostRecentNoteForProvider(
  docs: DocumentReferenceResource[],
  providerContext: ExtractedProviderContext
): RetrievedNoteMetadata | null {
  return filterNotesForProvider(docs, providerContext)[0] ?? null;
}

/**
 * Always GET Binary from the FHIR R4 base. VA DocumentReference attachment URLs
 * often omit `/r4` (`.../clinical-fhir/v1/Binary/{id}`), which fails in the browser.
 */
export function resolveBinaryRequestUrl(params: {
  binaryUrl?: string;
  binaryId?: string;
  site: string;
  fhirUrl?: string;
}): string {
  const fhirUrl = (params.fhirUrl || getFhirBaseUrl()).replace(/\/+$/, "");
  const fromUrl = (params.binaryUrl || "").match(/Binary\/([^/?#]+)/)?.[1] || "";
  const rawId = fromUrl || params.binaryId || params.binaryUrl || "";
  const cleanId = rawId.replace(/^\/?(Binary\/)?/, "").trim();

  if (!cleanId || cleanId.startsWith("http")) {
    throw new Error("Missing binary note identifier.");
  }

  const requestUrl = new URL(`${fhirUrl}/Binary/${encodeURIComponent(cleanId)}`);
  requestUrl.searchParams.set("-health-care-system", params.site);
  return requestUrl.toString();
}

function fhirReadHeaders(accessToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/fhir+json",
  };
}

/**
 * Fetches the binary note text from the FHIR Binary endpoint.
 */
export async function fetchBinaryNoteContent(params: {
  binaryUrl?: string;
  binaryId?: string;
  site: string;
  accessToken: string;
  fhirUrl?: string;
}): Promise<{ text: string; rawBinary: Record<string, unknown> }> {
  const requestUrl = resolveBinaryRequestUrl(params);

  let response: Response;
  try {
    response = await fetch(requestUrl, {
      method: "GET",
      headers: fhirReadHeaders(params.accessToken),
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Failed to fetch";
    throw new Error(`Unable to reach VA FHIR Binary at ${requestUrl}. ${reason}`);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = (errorBody?.issue?.[0]?.diagnostics ||
      errorBody?.issue?.[0]?.details?.text ||
      errorBody?.message ||
      `VA FHIR Binary request failed (${response.status})`) as string;
    throw new Error(message);
  }

  const rawBinary = await response.json();
  const base64Data = rawBinary.data || "";
  const decodedText = decodeBase64Utf8(base64Data);

  return {
    text: decodedText,
    rawBinary,
  };
}

export interface DocumentListResult {
  metadatas: RetrievedNoteMetadata[];
  lastProviderNote: RetrievedNoteMetadata | null;
  providerContext: ExtractedProviderContext;
  accessToken: string;
}

export interface NoteListRequestParams {
  patientIcn?: string;
  preferredSta3n?: string;
  onProgress?: (progress: NoteRetrievalProgress) => void;
}

function reportProgress(
  onProgress: NoteListRequestParams["onProgress"],
  step: NoteRetrievalStep,
  message: string
) {
  if (onProgress) {
    onProgress({ step, message });
  }
}

/**
 * Fetches the VA token, searches DocumentReferences, and holds signed note metadata.
 * All Notes uses every signed author. Get Last Note uses lastProviderNote (this DUZ only).
 * Does not fetch Binary content.
 */
export async function listDocumentReferencesForPatient(
  params: NoteListRequestParams
): Promise<DocumentListResult> {
  reportProgress(params.onProgress, "connecting", "Connecting to VA and verifying session...");

  const accessToken = await getVAAccessToken();
  if (!accessToken) {
    throw new Error("Unable to retrieve VA access token.");
  }

  const providerContext = extractProviderContextFromToken(accessToken, params.preferredSta3n);
  const patientIcn = (params.patientIcn || "").trim();

  if (!patientIcn) {
    throw new Error("Patient ICN is required to search clinical documents.");
  }

  reportProgress(params.onProgress, "searching", "Searching patient clinical documents in VA...");

  const docs = await fetchDocumentReferences({
    patientIcn,
    site: providerContext.sta3n,
    accessToken,
  });

  const metadatas = filterSignedNotes(docs);
  if (metadatas.length === 0) {
    throw new Error(NO_SIGNED_NOTES_MESSAGE);
  }

  return {
    metadatas,
    lastProviderNote: filterMostRecentNoteForProvider(docs, providerContext),
    providerContext,
    accessToken,
  };
}

/**
 * Fetches and decodes Binary content for selected notes concurrently.
 * Worker count equals the number of selected notes, capped at 5.
 */
export async function retrieveNotesByMetadata(params: {
  metadatas: RetrievedNoteMetadata[];
  providerContext: ExtractedProviderContext;
  accessToken?: string;
  onProgress?: (progress: NoteRetrievalProgress) => void;
}): Promise<DecodedNoteResult[]> {
  if (!params.metadatas.length) {
    throw new Error("Select at least one note to retrieve.");
  }

  if (params.metadatas.length > MAX_CONCURRENT_NOTE_FETCHES) {
    throw new Error(`You can retrieve up to ${MAX_CONCURRENT_NOTE_FETCHES} notes at a time.`);
  }

  const accessToken = params.accessToken || (await getVAAccessToken());
  if (!accessToken) {
    throw new Error("Unable to retrieve VA access token.");
  }

  reportProgress(params.onProgress, "retrieving", "Retrieving note content...");

  const results = await Promise.all(
    params.metadatas.map(async (metadata) => {
      const { text, rawBinary } = await fetchBinaryNoteContent({
        binaryUrl: metadata.binaryUrl,
        binaryId: metadata.binaryId,
        site: params.providerContext.sta3n,
        accessToken,
      });

      return {
        text,
        metadata,
        rawBinary,
      };
    })
  );

  reportProgress(params.onProgress, "decoding", "Decoding note content...");
  reportProgress(params.onProgress, "complete", "Note retrieved successfully.");

  return results;
}

/**
 * Orchestrates last-signed retrieval:
 * 1. Fetch VA access token
 * 2. Extract provider DUZ & Sta3n
 * 3. Search DocumentReferences for patient
 * 4. Filter for provider's latest signed note
 * 5. Fetch Binary note and decode UTF-8 text on client
 */
export async function retrieveMostRecentSignedNote(params: {
  patientIcn?: string;
  preferredSta3n?: string;
  patientName?: string;
  doctorName?: string;
  onProgress?: (progress: NoteRetrievalProgress) => void;
}): Promise<DecodedNoteResult> {
  const list = await listDocumentReferencesForPatient(params);

  if (!list.lastProviderNote) {
    throw new Error(NO_PROVIDER_NOTES_MESSAGE);
  }

  reportProgress(params.onProgress, "locating", "Locating your most recent signed note...");

  const results = await retrieveNotesByMetadata({
    metadatas: [list.lastProviderNote],
    providerContext: list.providerContext,
    accessToken: list.accessToken,
    onProgress: params.onProgress,
  });

  return results[0];
}
